import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Table, Badge, Input, ButtonGroup, Button } from "reactstrap";
import { customersReport } from "../service";
import staticData from "../data/customers_sample.json";
import { getRelativeTime } from "../../common/DateHelpers";

const USE_STATIC_DATA = process.env.REACT_APP_ENVIRONMENT === "production" ? false : false;
const formatCurrency = (value) => {
  if (!value || value === 0) return "-";
  return `₹${(value / 100000).toFixed(2)}L`;
};

const transformProjectsData = (data) => {
  const { organizations, orders } = data;

  // Filter orders where placed_date is null/blank
  const unplacedOrders = orders.filter((order) => !order.placed_date);

  return unplacedOrders.map((order) => {
    const org = organizations[order.organization_id.toString()];
    return {
      organization_id: order.organization_id,
      organization_name: org.name,
      project_id: order.project_id,
      project_name: order.project_name,
      value: order.value,
      status: order.order_status || "draft",
      last_activity_at: order.last_activity_at,
    };
  });
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("draft");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = USE_STATIC_DATA ? staticData : await customersReport();
        setProjects(transformProjectsData(data));
      } catch (error) {
        console.error("Failed to load projects:", error);
      }
    };
    loadData();
  }, []);

  // Get unique customers for dropdown
  const uniqueCustomers = useMemo(() => {
    const customers = [...new Set(projects.map((p) => p.organization_name))].sort();
    return customers;
  }, [projects]);

  // Get unique statuses from data
  const availableStatuses = useMemo(() => {
    const statuses = [...new Set(projects.map((p) => p.status))].filter(Boolean).sort();
    return statuses;
  }, [projects]);

  // Filter projects based on selected filters
  const filteredProjects = useMemo(() => {
    let filtered = projects;

    // Filter by customer
    if (selectedCustomer !== "all") {
      filtered = filtered.filter((p) => p.organization_name === selectedCustomer);
    }

    // Filter by status (single select)
    if (selectedStatus) {
      filtered = filtered.filter((p) => p.status === selectedStatus);
    }

    return filtered;
  }, [projects, selectedCustomer, selectedStatus]);

  // Sort projects
  const sortedProjects = useMemo(() => {
    if (!sortConfig.key) return filteredProjects;

    const sorted = [...filteredProjects].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "value") {
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (sortConfig.key === "organization_name" || sortConfig.key === "status") {
        aVal = (aVal || "").toString().toLowerCase();
        bVal = (bVal || "").toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }

      if (sortConfig.key === "last_activity_at") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });

    return sorted;
  }, [filteredProjects, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        direction: "asc",
      };
    });
  };

  const selectStatus = (status) => {
    // Radio button behavior: clicking the same status deselects it, otherwise selects the new one
    setSelectedStatus((prev) => (prev === status ? null : status));
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const statusPills = ["draft", "finalised", "approved", "confirmed"];

  // Map status values to display names
  const statusDisplayMap = {
    draft: "Draft",
    finalised: "Validation",
    approved: "Approved",
    confirmed: "Confirmed",
  };

  return (
    <Row className="border p-3 bg-white rounded">
      <Col className="d-flex gap-2 align-items-center mb-2 flex-wrap justify-content-between">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Input
            type="select"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            className="w-auto fs-12px"
          >
            <option value="all">All Customers</option>
            {uniqueCustomers.map((customer) => (
              <option key={customer} value={customer}>
                {customer}
              </option>
            ))}
          </Input>
          <ButtonGroup>
            {statusPills.map((status) => {
              const isSelected = selectedStatus === status;
              const hasData = availableStatuses.includes(status);
              return (
                <Button
                  key={status}
                  color={isSelected? "primary": "outline-primary"}
                  onClick={() => selectStatus(status)}
                >
                  {statusDisplayMap[status]}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
      </Col>
      <Table bordered responsive className="align-middle" style={{ fontSize: "0.9rem" }}>
        <thead>
          <tr>
            <th
              className="bg-secondary text-white text-start"
              role="button"
              onClick={() => handleSort("organization_name")}
              style={{ cursor: "pointer" }}
            >
              Customer {renderSortIcon("organization_name")}
            </th>
            <th className="bg-secondary text-white text-start">Project</th>
            <th
              className="bg-secondary text-white text-center"
              role="button"
              onClick={() => handleSort("value")}
              style={{ cursor: "pointer", width: "150px" }}
            >
              Value {renderSortIcon("value")}
            </th>
            <th
              className="bg-secondary text-white text-center"
              role="button"
              onClick={() => handleSort("status")}
              style={{ cursor: "pointer", width: "150px" }}
            >
              Status {renderSortIcon("status")}
            </th>
            <th
              className="bg-secondary text-white text-center"
              role="button"
              onClick={() => handleSort("last_activity_at")}
              style={{ cursor: "pointer", width: "150px" }}
            >
              Last Activity {renderSortIcon("last_activity_at")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedProjects.length > 0 ? (
            sortedProjects.map((project, index) => (
              <tr key={`${project.project_id}-${index}`} className={index % 2 === 0 ? "table-light" : "table-white"}>
                <td className="text-start">{project.organization_name}</td>
                <td className="text-start">
                  {project.project_id ? (
                    <>
                      <a
                        href={`/projects/${project.project_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-dark text-decoration-none"
                      >
                        {project.project_name || "-"}
                      </a>{" "}
                      <span className="text-AAAAAA">#{project.project_id}</span>
                    </>
                  ) : (
                    project.project_name || "-"
                  )}
                </td>
                <td className="text-center" style={{ width: "150px" }}>{formatCurrency(project.value)}</td>
                <td className="text-center" style={{ width: "150px" }}>
                  <Badge color="secondary">
                    {statusDisplayMap[project.status]}
                  </Badge>
                </td>
                <td className="text-center" style={{width: "150px"}}>{getRelativeTime(project.last_activity_at)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="text-center text-muted py-4">
                No projects found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Row>
  );
};

export default Projects;

